import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageHeader } from "@/components/ui/design-system";
import { openSupport } from "@/lib/supportContext";
import { errorMessage } from "@/lib/api";
import {
  support,
  supportDate,
  supportKinds,
  supportStatuses,
  type SupportRecipient,
  type SupportRequest,
  type SupportStatus,
} from "@/lib/support";
import "@/components/Support.css";

function Status({ value }: { value: SupportStatus }) {
  return (
    <Badge tone={value === "novo" ? "bad" : value === "u_radu" ? "warn" : "ok"}>
      {supportStatuses[value]}
    </Badge>
  );
}
function PrivatePhoto({
  requestId,
  photo,
}: {
  requestId: string;
  photo: SupportRequest["photos"][number];
}) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false,
      objectUrl = "";
    void support
      .photo(requestId, photo.id)
      .then((blob) => {
        if (!cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Fotografija nije dostupna.");
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [requestId, photo.id]);
  return url ? (
    <a href={url} target="_blank" rel="noreferrer">
      <img className="support-photo" src={url} alt={photo.filename} />
    </a>
  ) : (
    <p>{error || "Učitavanje fotografije…"}</p>
  );
}
function RequestCard({
  row,
  detail = false,
}: {
  row: SupportRequest;
  detail?: boolean;
}) {
  const safeUrl =
    row.url.startsWith("/") &&
    !row.url.startsWith("//") &&
    !row.url.includes("\\")
      ? row.url
      : null;
  return (
    <div className="support-card">
      <div className="support-row">
        <Status value={row.status} />
        <span className="text-sm">Prijava #{row.number}</span>
        <Badge tone="info">
          {supportKinds.find((kind) => kind.value === row.kind)?.label}
        </Badge>
      </div>
      <p className="support-text">{row.text}</p>
      {detail ? (
        <>
          <dl className="support-dict">
            <dt>Tko</dt>
            <dd>
              {row.user_name} · {row.role}
            </dd>
            <dt>Ekran</dt>
            <dd>
              {safeUrl ? (
                <Link to={safeUrl}>{row.screen_name} ↗</Link>
              ) : (
                row.screen_name
              )}
            </dd>
            <dt>Kad</dt>
            <dd>{supportDate(row.created_at)} (Zagreb)</dd>
            <dt>Uređaj</dt>
            <dd>{row.user_agent}</dd>
            <dt>Greška</dt>
            <dd>
              {row.last_error ? (
                <span className="support-text">
                  {row.last_error.message} ·{" "}
                  {row.last_error.status ?? "bez odgovora"} ·{" "}
                  {row.last_error.endpoint}
                  {row.last_error.request_id &&
                    ` · ${row.last_error.request_id}`}
                </span>
              ) : (
                "—"
              )}
            </dd>
          </dl>
          {row.photos.map((photo) => (
            <PrivatePhoto key={photo.id} requestId={row.id} photo={photo} />
          ))}
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {row.user_name} · {row.screen_name} · {supportDate(row.created_at)}
          </p>
          <Link to={`/pomoc/prijave/${row.id}`}>
            Otvori prijavu #{row.number}
          </Link>
        </>
      )}
      {row.answer && (
        <div className="support-context">
          <strong>Odgovor</strong>
          <p className="support-text">{row.answer}</p>
          {row.answered_at && <small>{supportDate(row.answered_at)}</small>}
        </div>
      )}
    </div>
  );
}
export function SupportPage({ admin = false }: { admin?: boolean }) {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin =
    user?.roles.some((role) => role.code === "superadmin") ?? false;
  const [rows, setRows] = useState<SupportRequest[] | null>(null);
  const [status, setStatus] = useState<SupportStatus>("novo");
  const [error, setError] = useState("");
  useEffect(() => {
    if (admin && !isAdmin) return;
    let cancelled = false;
    void support
      .list()
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch((cause) => {
        if (!cancelled)
          setError(errorMessage(cause, "Prijave nije moguće učitati."));
      });
    return () => {
      cancelled = true;
    };
  }, [admin, isAdmin, location.key]);
  if (admin && !isAdmin) return <Navigate to="/pomoc" replace />;
  const ownRows = rows?.filter((row) => admin || row.user_id === user?.id);
  const shown = ownRows?.filter((row) => !admin || row.status === status);
  return (
    <div className="support-stack">
      <PageHeader
        title={admin ? "Prijave" : "Pomoć i podrška"}
        actions={
          <Button onClick={() => openSupport()}>
            Prijavi problem ili pitaj
          </Button>
        }
      />
      <Link className="help-link-button" to="/pomoc/upute">
        Sve upute
      </Link>
      <nav className="support-row" aria-label="Podrška">
        <Link to="/pomoc">Moje prijave</Link>
        {isAdmin && <Link to="/pomoc/prijave">Sve prijave</Link>}
      </nav>
      {admin ? (
        <div className="support-filters" aria-label="Stanje prijava">
          {(Object.keys(supportStatuses) as SupportStatus[]).map((value) => (
            <Button
              key={value}
              variant={status === value ? "primary" : "secondary"}
              aria-pressed={status === value}
              onClick={() => setStatus(value)}
            >
              {supportStatuses[value]} (
              {rows?.filter((row) => row.status === value).length ?? 0})
            </Button>
          ))}
        </div>
      ) : (
        <h2 className="text-lg font-semibold">Moje prijave</h2>
      )}
      {error && <p role="alert">{error}</p>}
      {rows === null && !error && <p role="status">Učitavanje prijava…</p>}
      {shown?.map((row) => (
        <RequestCard key={row.id} row={row} />
      ))}
      {shown?.length === 0 && (
        <p>Nema prijava{admin ? " u ovom stanju" : ""}.</p>
      )}
    </div>
  );
}
function SupportForwardForm({
  recipients,
  recipient,
  message,
  busy,
  onRecipientChange,
  onMessageChange,
  onSubmit,
}: {
  recipients: SupportRecipient[];
  recipient: string;
  message: string;
  busy: boolean;
  onRecipientChange: (value: string) => void;
  onMessageChange: (value: string) => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className="support-stack rounded-lg border p-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <label>
        Primatelj
        <select
          required
          value={recipient}
          onChange={(event) => onRecipientChange(event.target.value)}
          disabled={busy}
        >
          <option value="">Odaberi korisnika</option>
          {recipients.map((item) => (
            <option key={item.id} value={item.id}>
              {item.full_name} · {item.role}
            </option>
          ))}
        </select>
      </label>
      <label>
        Kratka poruka
        <textarea
          required
          minLength={3}
          maxLength={2000}
          value={message}
          onChange={(event) => onMessageChange(event.target.value)}
          disabled={busy}
        />
      </label>
      <p className="text-sm">
        Šalje se tekst i kontekst, bez fotografija i pristupa prijavama. Odgovor
        korisniku i dalje šalje Mario.
      </p>
      <Button
        type="submit"
        disabled={busy || !recipient || message.trim().length < 3}
      >
        Pošalji proslijeđenu prijavu
      </Button>
    </form>
  );
}

function SupportAnswerForm({
  answer,
  faq,
  busy,
  onAnswerChange,
  onFaqChange,
  onResolve,
  onWorking,
  onForward,
  children,
}: {
  answer: string;
  faq: boolean;
  busy: boolean;
  onAnswerChange: (value: string) => void;
  onFaqChange: (value: boolean) => void;
  onResolve: () => void;
  onWorking: () => void;
  onForward: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="support-card support-form">
      <h2 className="text-lg font-semibold">Odgovori korisniku</h2>
      <label>
        Odgovor
        <textarea
          value={answer}
          maxLength={10000}
          onChange={(event) => onAnswerChange(event.target.value)}
          disabled={busy}
        />
      </label>
      <label className="support-check">
        <input
          type="checkbox"
          checked={faq}
          onChange={(event) => onFaqChange(event.target.checked)}
          disabled={busy}
        />
        Dodaj u česta pitanja
      </label>
      <div className="support-row">
        <Button
          disabled={busy || answer.trim().length < 3}
          onClick={() => onResolve()}
        >
          Odgovori i riješi
        </Button>
        <Button variant="secondary" disabled={busy} onClick={() => onWorking()}>
          U radu
        </Button>
        <Button variant="secondary" disabled={busy} onClick={() => onForward()}>
          Proslijedi
        </Button>
      </div>
      {children}
    </section>
  );
}

export function SupportDetailPage() {
  const { id = "" } = useParams();
  const { user } = useAuth();
  const isAdmin =
    user?.roles.some((role) => role.code === "superadmin") ?? false;
  const [row, setRow] = useState<SupportRequest | null>(null);
  const [answer, setAnswer] = useState("");
  const [faq, setFaq] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [forwarding, setForwarding] = useState(false);
  const [recipients, setRecipients] = useState<SupportRecipient[]>([]);
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    let cancelled = false;
    void support
      .get(id)
      .then((result) => {
        if (!cancelled) {
          setRow(result);
          setAnswer(result.answer ?? "");
          setFaq(result.add_to_faq);
        }
      })
      .catch((cause) => {
        if (!cancelled) setError(errorMessage(cause, "Prijava nije dostupna."));
      });
    return () => {
      cancelled = true;
    };
  }, [id]);
  async function action(task: () => Promise<SupportRequest>, success: string) {
    if (busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await task();
      setRow(result);
      setNotice(
        result.mail_delivered === false
          ? `${success} Slanje maila čeka ponovni pokušaj.`
          : success,
      );
      setForwarding(false);
    } catch (cause) {
      setError(errorMessage(cause, "Promjena nije spremljena."));
    } finally {
      setBusy(false);
    }
  }
  async function showForwarding() {
    setForwarding(true);
    setError("");
    try {
      setRecipients(await support.recipients());
    } catch (cause) {
      setError(errorMessage(cause, "Primatelje nije moguće učitati."));
    }
  }
  return (
    <div className="support-stack">
      <PageHeader
        title={row ? `Prijava #${row.number}` : "Prijava"}
        breadcrumb={[
          { label: "Moje prijave", to: "/pomoc" },
          ...(isAdmin ? [{ label: "Sve prijave", to: "/pomoc/prijave" }] : []),
        ]}
      />
      {error && <p role="alert">{error}</p>}
      {notice && (
        <p role="status" className="support-success">
          {notice}
        </p>
      )}
      {!row && !error && <p role="status">Učitavanje prijave…</p>}
      {row && (
        <>
          <RequestCard row={row} detail />
          {isAdmin && (
            <SupportAnswerForm
              answer={answer}
              faq={faq}
              busy={busy}
              onAnswerChange={setAnswer}
              onFaqChange={setFaq}
              onResolve={() =>
                void action(
                  () =>
                    support.update(id, {
                      answer: answer.trim(),
                      status: "rijeseno",
                      add_to_faq: faq,
                    }),
                  "Odgovor je spremljen i prijava riješena.",
                )
              }
              onWorking={() =>
                void action(
                  () =>
                    support.update(id, {
                      status: "u_radu",
                      add_to_faq: faq,
                    }),
                  "Prijava je u radu.",
                )
              }
              onForward={() => void showForwarding()}
            >
              {forwarding && (
                <SupportForwardForm
                  recipients={recipients}
                  recipient={recipient}
                  message={message}
                  busy={busy}
                  onRecipientChange={setRecipient}
                  onMessageChange={setMessage}
                  onSubmit={() =>
                    void action(
                      () => support.forward(id, recipient, message.trim()),
                      "Prijava je proslijeđena.",
                    )
                  }
                />
              )}
            </SupportAnswerForm>
          )}
          <details className="support-card">
            <summary>Dnevnik</summary>
            {row.events.length ? (
              row.events.map((event) => (
                <p key={event.id}>
                  {supportDate(event.created_at)} · {event.text}
                </p>
              ))
            ) : (
              <p>Prijava je zaprimljena.</p>
            )}
          </details>
        </>
      )}
    </div>
  );
}
export function SupportSettings() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void support
      .settings()
      .then((data) => {
        if (!cancelled) {
          setEmail(data.email ?? "");
          setLoaded(true);
        }
      })
      .catch((cause) => {
        if (!cancelled)
          setError(errorMessage(cause, "Postavka nije dostupna."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  async function save() {
    if (!loaded || busy) return;
    setBusy(true);
    setSaved(false);
    setError("");
    try {
      await support.saveSettings(email.trim() || null);
      setSaved(true);
    } catch (cause) {
      setError(errorMessage(cause, "Mail za prijave nije spremljen."));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="support-card support-form">
      <h2 className="text-lg font-semibold">Pomoć i podrška</h2>
      {error && <p role="alert">{error}</p>}
      <form
        className="support-stack"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label>
          Mail za prijave
          <input
            type="email"
            maxLength={320}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setSaved(false);
            }}
            disabled={loading || busy || !loaded}
          />
        </label>
        <p className="text-sm">
          Prazno polje: obavijesti se šalju superadminima.
        </p>
        <Button type="submit" disabled={loading || busy || !loaded}>
          Spremi mail za prijave
        </Button>
        {saved && <p role="status">Mail za prijave je spremljen.</p>}
      </form>
    </section>
  );
}
